/**
 * HTML Sanitization Utility
 * 
 * Uses DOMPurify to safely sanitize HTML content before rendering
 * with dangerouslySetInnerHTML. Prevents XSS attacks from untrusted
 * sources like AI-generated content.
 */

import DOMPurify from 'dompurify';

/**
 * Default allowed tags for report/markdown content
 * These are safe structural and formatting tags
 */
const DEFAULT_ALLOWED_TAGS = [
  'p', 'br', 'strong', 'b', 'em', 'i',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'span', 'div',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
  'blockquote', 'pre', 'code',
];

/**
 * Default allowed attributes for safe HTML rendering
 */
const DEFAULT_ALLOWED_ATTR = ['class', 'style'];

/**
 * CSS properties that are allowed in style attributes
 * Restricts to safe styling properties
 */
const ALLOWED_CSS_PROPERTIES = [
  'color', 'background-color', 'font-weight', 'font-style', 'font-size',
  'text-align', 'text-decoration', 'margin', 'padding', 'border',
  'margin-top', 'margin-bottom', 'margin-left', 'margin-right',
  'padding-top', 'padding-bottom', 'padding-left', 'padding-right',
];

/**
 * Sanitize HTML content for safe rendering
 * 
 * @param html - The HTML string to sanitize
 * @param options - Optional configuration for allowed tags/attributes
 * @returns Sanitized HTML string safe for dangerouslySetInnerHTML
 */
export function sanitizeHtml(
  html: string,
  options?: {
    allowedTags?: string[];
    allowedAttr?: string[];
    allowStyle?: boolean;
  }
): string {
  if (!html) return '';

  const config = {
    ALLOWED_TAGS: options?.allowedTags ?? DEFAULT_ALLOWED_TAGS,
    ALLOWED_ATTR: options?.allowedAttr ?? DEFAULT_ALLOWED_ATTR,
    // Disallow dangerous URI schemes
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    // Remove scripts and other dangerous content
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: options?.allowStyle 
      ? ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur']
      : ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur', 'style'],
  };

  return DOMPurify.sanitize(html, config) as string;
}

/**
 * Sanitize HTML specifically for report content
 * Allows common formatting while blocking dangerous content
 */
export function sanitizeReportHtml(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: DEFAULT_ALLOWED_TAGS,
    allowedAttr: ['class'],
    allowStyle: false,
  });
}
